import type { ComponentProps } from 'react';

import { Link, Text } from '@change/ds-core-ui/next';
import {
	FooterLayout,
	FooterLayoutBottom,
	FooterLayoutCopyright,
	FooterLayoutLanguageSwitcher,
	FooterLayoutSection,
	FooterLayoutSeoContent,
} from '@change/ds-layout/next';

import { getLocaleInfo } from '@change-corgi/config/locales';
import { Translate, TranslateHtml, useI18n } from '@change-corgi/core/react/i18n';

import { HOMEPAGE_HREF_LANGS } from 'config/routes-l10n';

import { useFooter } from './hooks/useFooter';

export const Footer: React.FC<ComponentProps<'div'>> = (props) => {
	const {
		data: { links, currentLocale, locales, currentYear },
		actions: { handleLocaleChange },
	} = useFooter();
	const { translate } = useI18n();

	return (
		<FooterLayout {...props}>
			{links.map((linkGroup) => (
				<FooterLayoutSection key={linkGroup.title} title={linkGroup.title}>
					{linkGroup.links.map((link) => (
						<Text key={link.title} as="h3">
							<Link href={link.href} target={link.target} variant="secondary" underline={false}>
								{link.title}
							</Link>
						</Text>
					))}
				</FooterLayoutSection>
			))}
			<FooterLayoutBottom>
				<FooterLayoutLanguageSwitcher
					value={currentLocale}
					languages={locales}
					onChange={handleLocaleChange}
					ariaLabel={translate('fe.components.footer.locale')}
					data-qa="footer-locale-switcher"
				/>
				<FooterLayoutSeoContent>
					<noscript>
						<ul>
							{HOMEPAGE_HREF_LANGS.map(({ slug, lang }) => {
								const locale = getLocaleInfo(lang);

								return (
									<li key={slug}>
										{/* eslint-disable-next-line react/forbid-elements, @change-corgi/require-data-qa-attribute */}
										<a href={`https://www.change.org/${slug}`}>{locale.display}</a>
									</li>
								);
							})}
						</ul>
					</noscript>
				</FooterLayoutSeoContent>
				<FooterLayoutCopyright>
					<Text fontWeight="bold" size="small" className="text-typography-strong">
						<Translate value="fe.components.footer.copyright" replacements={{ year: currentYear }} />
					</Text>
					<Text size="small" className="text-typography-strong">
						<TranslateHtml
							value="fe.components.footer.recaptcha.branding_text"
							replacements={{
								googlePrivacyPolicyLink: (
									<Link href="https://policies.google.com/privacy" target="_blank" size="small">
										<Translate value="fe.components.footer.recaptcha.privacy_policy_link_text" />
									</Link>
								),
								googleToSLink: (
									<Link href="https://policies.google.com/terms" target="_blank" size="small">
										<Translate value="fe.components.footer.recaptcha.terms_of_service_link_text" />
									</Link>
								),
							}}
						/>
					</Text>
				</FooterLayoutCopyright>
			</FooterLayoutBottom>
		</FooterLayout>
	);
};
